import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as errorCodes from '../common/errorCodes';
import * as actions from './actionCreators';
import { ERR_MSG } from '../loginpages/actionTypes';
import * as JspUtil from '../common/JspUtil';
import PagePasswordRules from './PagePasswordRules';
import PasswordMeter from './PasswordMeter';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { useJspVariables, useNavigation, useAppContext, useAuth, useLoader, useLoginFlowState, useLoginInput, useDebugRender, usePageTitle } from '../hooks';
import { Dropdown, IceIcon, PageContainer, PageHeader, PasswordInput, TextInput, DialogTitleWithClose } from '../components';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const PageChangePassword = () => {
    useDebugRender('PageChangePassword');
    usePageTitle('Change Password');
    const { jspVariables, launcher, panelStyle } = useJspVariables();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { toPageForgotPassword } = useNavigation({ launcher });
    const { appKey, getLoginApp, getUiMsg, getLabelFromJspVariables, onAppNameChange } = useAppContext(jspVariables);
    const { userName: contextUserName } = useLoginInput();
    const { showLoader, hideLoader } = useLoader();

    const { authenticatedUser, authenticatedApp, ssoEnvironment, isAuthenticated, logout } = useAuth();
    const { isPasswordExpired } = useLoginFlowState();
    const authenticatedUserName = useSelector(state => state.authenticateReducer.authenticatedUserName);
    const userAppConfigs = useSelector(state => state.authenticateReducer.userDashboardConfig.appConfigs);
    const errorMessage = useSelector(state => state.authenticateReducer.errorMessage);

    const [userName, setUserNameLocal] = useState(contextUserName || '');

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newConfirmPassword, setNewConfirmPassword] = useState('');
    const [showAppSelector, setShowAppSelector] = useState(true);
    const [useStoredTemp, setUseStoredTemp] = useState('N');
    const [pwnedPassword, setPwnedPassword] = useState(false);
    const [selectApp, setSelectApp] = useState(false);
    const [selectAppOptions, setSelectAppOptions] = useState([]);
    const [passwordChanged, setPasswordChanged] = useState(false);
    const [alert, setAlert] = useState(null);
    const [errors, setErrors] = useState({});

    const showOldPassword = !JspUtil.isValueAvailable(jspVariables.jspCode);

    useEffect(() => {
        if (authenticatedUserName) {
            setUserNameLocal(authenticatedUserName);
        } else {
            if (JspUtil.isValueAvailable(jspVariables.jspUserId))
                setUserNameLocal(jspVariables.jspUserId);
        }

        if (authenticatedApp) {
            if (authenticatedApp !== 'ANY') {
                onAppNameChange(authenticatedApp);
                setShowAppSelector(false);
            }
        } else {
            if (JspUtil.isValueAvailable(jspVariables.jspApp)) {
                if (jspVariables.jspApp !== 'ANY') {
                    const resolvedApp = (launcher && jspVariables.jspLoginApp === constants.APP_LAUNCHER)
                        ? jspVariables.jspLoginApp
                        : jspVariables.jspApp;
                    onAppNameChange(resolvedApp);
                    setShowAppSelector(false);
                }
            }
        }
        if (launcher) {
            setShowAppSelector(false);
        }
        if (authenticatedUser) {
            const authUserName = authenticatedUser.user;
            const authApp = authenticatedUser.app;
            if (authUserName && authApp) {
                setUserNameLocal(authUserName);
                onAppNameChange(authApp);
            }
        }
        if (jspVariables.jspUseStoredTemp === 'Y') {
            setUseStoredTemp('Y');
        }
    }, []);

    // Populate fields when auth resolves after page reload (checkPermissions is async)
    useEffect(() => {
        if (authenticatedUser) {
            const authUserName = authenticatedUser.user;
            const authApp = authenticatedUser.app;
            if (authUserName && !userName) {
                setUserNameLocal(authUserName);
            }
            // 'ANY' is the app-list bootstrap default, i.e. no app resolved yet — so
            // it must not block adopting the authenticated user's actual app.
            if (authApp && authApp !== 'ANY' && (!appKey || appKey === 'ANY')) {
                onAppNameChange(authApp);
                setShowAppSelector(false);
            }
        }
    }, [authenticatedUser]);

    const checkPwnedPassword = async (password) => {
        try {
            const resp = await actions.checkPassword(password);
            if (resp.data && resp.data.pwned === 'Y') {
                setPwnedPassword(true);
            } else {
                setPwnedPassword(false);
            }
        } catch (_) {
            // Pwned check failure is non-critical
        }
    };

    const passwordMismatch = newConfirmPassword.length > 0 && newPassword !== newConfirmPassword;


    const validate = () => {
        const fieldErrors = {};
        if (!userName) fieldErrors.userName = errorCodes.MSG_FIELD_REQUIRED();
        if (showOldPassword && !oldPassword) fieldErrors.oldPassword = errorCodes.MSG_FIELD_REQUIRED();
        if (!newPassword) fieldErrors.newPassword = errorCodes.MSG_FIELD_REQUIRED();
        else if (pwnedPassword) fieldErrors.newPassword = getLabelFromJspVariables('alert_password_pwned');
        if (!newConfirmPassword) fieldErrors.newConfirmPassword = errorCodes.MSG_FIELD_REQUIRED();
        if (newPassword && newConfirmPassword && newPassword !== newConfirmPassword) fieldErrors.newConfirmPassword = errorCodes.MSG_PASSWORDS_DO_NOT_MATCH();
        setErrors(fieldErrors);
        return Object.keys(fieldErrors).length === 0;
    };

    const clearFieldError = (field) => {
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const changeUserPassword = async () => {
        setAlert(null);
        if (!validate()) return;

        let app = appKey;
        if (getLoginApp() === 'iceIM') app = 'iceIM';
        if (launcher) app = constants.APP_LAUNCHER;

        let oldPass = oldPassword;
        if (!oldPassword && JspUtil.isValueAvailable(jspVariables.jspCode)) {
            oldPass = jspVariables.jspCode;
        }
        let data = null;
        if (typeof jspData !== 'undefined' && JspUtil.isValueAvailable(jspData)) {
            data = jspData;
        }

        showLoader('Changing password...');
        try {
            setSelectApp(false);
            setSelectAppOptions([]);
            await actions.changeUserPassword(userName, app && app !== 'ANY' ? app : null, oldPass, newPassword, useStoredTemp, data);
            dispatch({ type: ERR_MSG, data: '' });
            setPasswordChanged(true);
        } catch (resp) {
            if (resp.code == errorCodes.ERR_TEMP_PASSWORD_EXPIRED || resp.code == errorCodes.ERR_APIUSER_TEMP_PASSWORD_EXPIRED) {
                navigate(constants.ROUTE_PAGE_FORGOT_PASSWORD, {
                    state: { pageMessage: getLabelFromJspVariables('note_temp_password_link_expired') },
                });
            } else if (resp.code == errorCodes.ERR_ACCOUNT_LOCKED) {
                setAlert({ message: JspUtil.getLabelFromJspVariables('note_account_locked'), severity: 'error' });
            } else if (resp.code == errorCodes.ERR_USER_NEEDS_TO_SELECT_APP) {
                setSelectApp(true);
                setSelectAppOptions(resp.data || []);
            } else {
                setAlert({ message: resp.message || 'Password change failed', severity: 'error' });
            }
        } finally {
            hideLoader();
        }
    };

    const handleSuccessOk = () => {
        setPasswordChanged(false);
        // Email-link / external change-password flows set jspRedirectUrl to the
        // originating app. Send the user back there instead of forcing re-login.
        // Source apps are expected to trigger login flow on arrial.
        if (JspUtil.isValueAvailable(jspVariables.jspRedirectUrl)) {
            location.href = jspVariables.jspRedirectUrl;
            return;
        }
        logout(authenticatedUser?.user);
    };

    const getGlobalAppDisplayNames = () => {
        const configs = userAppConfigs;
        if (!isAuthenticated || !Array.isArray(configs) || configs.length === 0) {
            return [];
        }
        return configs.map(cfg => {
            let displayName = '';
            if (cfg && cfg.icon) {
                try {
                    const iconObj = (typeof cfg.icon === 'string') ? JSON.parse(cfg.icon) : cfg.icon;
                    if (iconObj) {
                        if (typeof iconObj.hiddenFromDisplay !== 'undefined' && iconObj.hiddenFromDisplay) return null;
                        displayName = iconObj.iconDisplayName || iconObj.displayName || iconObj.name || '';
                    }
                } catch (e) { /* ignore */ }
            }
            if (cfg.coverAppUser) return null;
            if (!displayName && typeof cfg.app === 'string' && cfg.app.indexOf('|') > -1) {
                displayName = cfg.app.split('|')[1].trim();
            }
            if (!displayName) {
                displayName = cfg.appName || cfg.app || '';
            }
            return displayName;
        }).filter(n => n && n.trim() !== '')
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    };

    const standalone = jspVariables.jspPage === 'CHANGE_PASSWORD';
    let noteOne = getUiMsg(constants.PAGE_PASSWORD_CHANGE_NOTE_ONE);
    const noteTwo = getUiMsg(constants.PAGE_PASSWORD_CHANGE_NOTE_TWO);
    noteOne = noteOne ? noteOne : 'Note: New Global ID password change will be applied for the following applications';

    return (
        <PageContainer id="pageChangePassword" dataAutomationId="page_change_password">
            <MuiDialog open={passwordChanged} maxWidth="sm" fullWidth
                aria-labelledby="password-changed-title"
                aria-describedby="password-changed-description">
                <DialogTitleWithClose id="password-changed-title" severity="success" onClose={handleSuccessOk}
                    dataAutomationId="password_changed_title">{getLabelFromJspVariables('title_password_changed')}</DialogTitleWithClose>
                <DialogContent id="password-changed-description">
                    <Alert severity="success" aria-live="polite" sx={{ mb: 1 }}>{getLabelFromJspVariables('alert_password_changed_success')}</Alert>
                    <Alert severity="info" aria-live="polite">{getLabelFromJspVariables('note_sessions_signed_out')}</Alert>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleSuccessOk}
                        data-automation-id="password_changed_ok_btn">{getLabelFromJspVariables('action_ok')}</Button>
                </DialogActions>
            </MuiDialog>
            <Box sx={panelStyle}>
                {ssoEnvironment && (ssoEnvironment.isProd === 'false') &&
                    <Box className="help-notes"><p style={{ color: '#c00700' }}>{`IMPORTANT NOTE: ${getLabelFromJspVariables('non_prod_change_password_note')}`}</p></Box>
                }
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="change_password_header">{getLabelFromJspVariables('change_password')}</PageHeader>
                {errorMessage && errorMessage.trim() !== '' &&
                    <Alert severity="error" sx={{ mb: 1 }} data-automation-id="password_expired_alert">
                        {errorMessage}
                    </Alert>}
                {alert && <Alert id="pageChangePasswordAlert" data-automation-id="page_change_password_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                <Box component="form" id="formChangePassword" data-automation-id="form_change_password"
                    onSubmit={(e) => { e.preventDefault(); changeUserPassword(); }}>
                    <TextInput
                        label={getLabelFromJspVariables('label_userid')}
                        id="username_input"
                        dataAutomationId="username_input"
                        name="username"
                        autoComplete="username"
                        value={userName}
                        required={true}
                        disabled={!!authenticatedUser}
                        error={!!errors.userName}
                        helperText={errors.userName || ''}
                        onChange={e => { setUserNameLocal(e.target.value); clearFieldError('userName'); }}
                    />
                    {showOldPassword &&
                        <PasswordInput
                            label={getLabelFromJspVariables('label_old_temporary_password')}
                            autoComplete="current-password"
                            value={oldPassword}
                            id="old_password_input"
                            dataAutomationId="old_password_input"
                            name="current-password"
                            required={true}
                            error={!!errors.oldPassword}
                            helperText={errors.oldPassword || ''}
                            onChange={e => { setOldPassword(e.target.value); clearFieldError('oldPassword'); }}
                        />
                    }
                    <PasswordInput
                        label={getLabelFromJspVariables('label_new_password')}
                        autoComplete="new-password"
                        value={newPassword}
                        id="new_password_input1"
                        dataAutomationId="new_password_input1"
                        name="new-password"
                        required={true}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword || ''}
                        onBlur={() => newPassword && checkPwnedPassword(newPassword)}
                        onChange={e => { setNewPassword(e.target.value); setPwnedPassword(false); clearFieldError('newPassword'); }}
                    />
                    <PasswordMeter password={newPassword} pwnedPassword={pwnedPassword} />
                    <PasswordInput
                        label={getLabelFromJspVariables('label_new_password_confirmation')}
                        autoComplete="new-password"
                        value={newConfirmPassword}
                        id="new_password_input2"
                        dataAutomationId="new_password_input2"
                        name="confirm-new-password"
                        required={true}
                        error={!!errors.newConfirmPassword || passwordMismatch}
                        helperText={errors.newConfirmPassword || (passwordMismatch ? errorCodes.MSG_PASSWORDS_DO_NOT_MATCH() : '')}
                        onChange={e => { setNewConfirmPassword(e.target.value); clearFieldError('newConfirmPassword'); }}
                    />
                    {!launcher && showAppSelector && selectApp &&
                        <Dropdown selectedValue={appKey}
                            labelProperty="name"
                            valueProperty="appKey"
                            id="appkey_dropdown"
                            name="appKey"
                            dataAutomationId="appkey_dropdown"
                            label={getLabelFromJspVariables('label_application')}
                            options={selectAppOptions} placeholder={getLabelFromJspVariables('label_select_application')}
                            updateValue={val => onAppNameChange(val)} />
                    }
                    {appKey === 'GLOBAL' && Array.isArray(userAppConfigs) && getGlobalAppDisplayNames().length > 1 &&
                        <Box>
                            <Typography component="span" sx={{ color: '#43a047', fontSize: '12px', fontWeight: 'bold', cursor: 'text', display: 'block', marginBottom: '4px' }} dangerouslySetInnerHTML={{ __html: noteOne }} />
                            <TextInput
                                id="page_config_input"
                                dataAutomationId="page_config_input"
                                value={getGlobalAppDisplayNames().join('\n')}
                                multiline
                                rows={6}
                                disabled
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#ffffff',
                                        '& textarea': {
                                            resize: 'none',
                                            whiteSpace: 'pre',
                                            cursor: 'default',
                                        },
                                    },
                                }}
                            />
                            {noteTwo && <Typography component="span" sx={{ color: '#43a047', fontSize: '12px', fontWeight: 'bold', cursor: 'text', display: 'block', marginTop: '4px' }} dangerouslySetInnerHTML={{ __html: noteTwo }} />}
                        </Box>
                    }
                    {isPasswordExpired ?
                        <Button
                            variant="contained"
                            fullWidth
                            aria-label="Forgot/Reset Password"
                            type="button"
                            id="loginPageForgotPassword"
                            onClick={toPageForgotPassword}
                            data-automation-id="change_password_forgot_password_btn"
                        >
                            {getLabelFromJspVariables('label_forgot_password')}
                        </Button>
                        :
                        <Button
                            variant="contained"
                            aria-label="Change Password"
                            fullWidth
                            type="submit"
                            data-automation-id="change_password_submit_btn"
                        >
                            {getLabelFromJspVariables('change_password')}
                        </Button>
                    }
                    {!standalone &&
                        <Box sx={{ display: 'flex', justifyContent: 'center', m: '0.5rem' }}>
                            <Link component="button" type="button"
                                onClick={() => isAuthenticated ? navigate(-1) : navigate(constants.ROUTE_PAGE_LOGIN)}
                                underline="hover"
                                data-automation-id="change_password_back_link"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    color: 'inherit',
                                }}>
                                <ChevronLeftIcon sx={{ fontSize: '1.25rem', color: '#000', strokeWidth: 2, stroke: '#000' }} />
                                {isAuthenticated ? 'Back' : 'Return to Login'}
                            </Link>
                        </Box>
                    }
                    <PagePasswordRules />
                </Box>
            </Box>
        </PageContainer>
    );
};

export default PageChangePassword;
